import { mapGetters } from 'vuex';
import styled, { css } from 'vue-styled-components';

import {
    COLORS,
    square,
    applyHoverAndPress,
    ABSOLUTE_FULL_AREA,
    ABSOLUTE_BOTH_CENTER,
} from '@/styles/common.styled';

import {
    FLEX_COLUMN_DIV,
    FLEX_VERTICAL_CENTER_DIV,
    GRAY_100_SPAN,
} from '@styles/common.styled.component';

import CloseIcon from '@svgIcons/close.svg';

import { Drawer } from 'ant-design-vue';
import { ButtonArea, ActionButton } from '@components/Common/Dialog';
import RingLoader from '@components/Common/RingLoader';
import ErrorDialogContent from '@components/Common/ErrorDialogContent';

import { fromEvent, combineLatest, empty, of } from 'rxjs';
import { filter, switchMap, pluck, map, takeUntil } from 'rxjs/operators';

export default {
    name: 'CommonDrawer',
    props: {
        headerText: String | Object,
        headerType: {
            type: String,
            default: 'normal', // 'warning'
        },
        onConfirm: {
            type: Function,
            default: R.identity,
        },
        onCancel: {
            type: Function,
            default: R.identity,
        },
        confirmDisabled: {
            type: Boolean,
            default: false,
        },
        loadingMask: {
            type: Boolean,
            default: false,
        },
        loadingMaskAdditionalUI: {
            type: [Object, Function, Array],
            default: () => null,
        },
        active: {
            type: Boolean,
            default: true,
        },
        editAreaPadding: {
            type: String,
            default: '20px 28px 0 28px',
        },
        confirmText: {
            type: String,
            default: '',
        },
        mask: {
            type: Boolean,
            default: false,
        },
        maskStyle: {
            type: Object,
            default() {
                return {};
            },
        },
        hasButtons: {
            type: Boolean,
            default: false,
        },
        zIndex: Number,
        disabledReason: String,
        hasClose: {
            type: Boolean,
            default: true,
        },
        closeOutside: {
            type: Boolean,
            default: false,
        },
        wrapStyle: {
            type: Object,
            default() {
                return {};
            },
        },
        hasXScroll: {
            type: Boolean,
            default: false,
        },
        closeBlockClasses: {
            type: Array,
            default() {
                return [];
            },
        },
        wrapClassName: {
            type: String,
            default: '',
        },
        width: String | Number,
        buttonTooltipPlacement: {
            type: String,
            default: 'top',
        },
        hasCancel: {
            type: Boolean,
            default: true,
        },
        placement: {
            type: String,
            default: 'right',
        },
        unblock: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            origin: null,
        };
    },
    mounted() {
        this.$nextTick(() => {
            this.observeCloseOutside$();
        });
    },
    computed: {
        ...mapGetters('view', ['subscriptionWarningVisible']),
    },
    render() {
        const {
            isMobileSize,
            active,
            hasButtons,
            onCancel,
            zIndex = 10,
            wrapTooltip,
            disabledReason,
            confirmDisabled,
            onConfirm,
            hasClose,
            wrapClassName,
            width,
            buttonTooltipPlacement,
            placement,
            unblock,
        } = this;

        const PROPS = {
            attrs: {
                destroyOnClose: true,
                maskClosable: true,
                mask: this.mask,
                visible: active,
                wrapClassName: [
                    'dashboard-edit-panel-wrapper',
                    wrapClassName,
                ].join(' '),
                width: isMobileSize ? '100vw' : width ?? '544',
                placement,
                keyboard: false,
                closable: false,
                zIndex,
                maskStyle: this.maskStyle,
                push: true,
                wrapStyle: this.wrapStyle,
            },
            on: {
                close: this.onCancel,
            },
            class: 'dashboard-drawer',
        };

        return (
            <Drawer {...PROPS}>
                <Wrapper
                    class="drawer-wrapper"
                    blocked={!unblock && this.subscriptionWarningVisible}
                >
                    {this.renderLoadingMask()}
                    <HeaderArea class="bottom-border drawer-header">
                        {this.$slots.header || (
                            <HeaderText>{this.headerText}</HeaderText>
                        )}
                        {hasClose && (
                            <CloseIcon
                                class="close-icon"
                                v-view-box
                                onClick={onCancel}
                            />
                        )}
                    </HeaderArea>
                    {this.$slots.info && (
                        <InfoArea class="bottom-border">
                            {this.$slots.info}
                        </InfoArea>
                    )}
                    <EditArea
                        editAreaPadding={this.editAreaPadding}
                        hasXScroll={this.hasXScroll}
                        class="dashboard-drawer-edit-area"
                    >
                        {this.$slots.default}
                    </EditArea>

                    {hasButtons && [
                        <BottomDummy />,
                        <BottomArea>
                            {this.$slots.buttonAreaSlot || (
                                <ButtonArea class="buttons-area">
                                    {this.hasCancel && (
                                        <ActionButton
                                            onClick={this.onCancel}
                                            type="cancel"
                                        >
                                            {this.$t('COMMON.cancel')}
                                        </ActionButton>
                                    )}

                                    {this.$slots.confirmButton ||
                                        wrapTooltip({
                                            visible:
                                                confirmDisabled &&
                                                !!disabledReason,
                                            title: disabledReason,
                                            placement: buttonTooltipPlacement,
                                            content: (
                                                <ActionButton
                                                    onClick={() => {
                                                        if (confirmDisabled) {
                                                            return;
                                                        }

                                                        onConfirm();
                                                    }}
                                                    isConfirm
                                                    disabled={confirmDisabled}
                                                >
                                                    {this.confirmText
                                                        ? this.confirmText
                                                        : this.$t(
                                                              'EDITABLE_DASHBOARD.edit_panel_update'
                                                          )}
                                                </ActionButton>
                                            ),
                                        })}
                                </ButtonArea>
                            )}
                        </BottomArea>,
                    ]}
                </Wrapper>
            </Drawer>
        );
    },
    methods: {
        renderLoadingMask() {
            if (!this.loadingMask) {
                return null;
            }

            const { loadingMaskAdditionalUI } = this;

            return (
                <LoadingMask>
                    <LoadingMaskContent>
                        <RingLoader />
                        {loadingMaskAdditionalUI}
                    </LoadingMaskContent>
                </LoadingMask>
            );
        },
        showErrorDialog(text) {
            text = text || this.$t('NEW_DASHBOARD.publishing_error_general');

            this.dialog({
                confirmText: this.$t('COMMON.go_back'),
                confirmType: 'secondary',
                hasCancelButton: false,
                child: <ErrorDialogContent mainText={text} />,
                confirmCb: () => {},
            });
        },
        observeCloseOutside$() {
            const { generateWatchNew$: gen$ } = this;

            const closeOutside$ = gen$('closeOutside').pipe(filter(R.identity));
            const active$ = gen$('active').pipe(filter(R.identity));

            const down$ = fromEvent(document, 'mousedown');
            const up$ = fromEvent(document, 'mouseup');
            const move$ = fromEvent(document, 'mousemove');

            const moved = (a, b) => Math.abs(a - b) > 8;

            const click$ = down$.pipe(
                switchMap(({ clientX: dX, clientY: dY }) =>
                    up$.pipe(
                        takeUntil(
                            move$.pipe(
                                switchMap((evt) => {
                                    const { clientX: mX, clientY: mY } = evt;

                                    return moved(dX, mX) || moved(dY, mY)
                                        ? of(evt)
                                        : empty();
                                })
                            )
                        )
                    )
                )
            );

            const obs$ = combineLatest(closeOutside$, active$).pipe(
                filter(R.all(R.identity)),
                switchMap(() => click$),
                pluck('target'),
                map((el) => [
                    el.closest('.dashboard-edit-panel-wrapper'),
                    ...R.map(
                        (v) => el.closest(`.${v}`),
                        this.closeBlockClasses
                    ),
                ]),
                map(R.filter(R.identity)),
                filter(R.isEmpty)
            );

            this.$subscribeTo(obs$, (v) => {
                const { onCancel = R.identity } = this;

                if (this.active && this.closeOutside) {
                    onCancel();
                }
            });
        },
    },
};

const Wrapper = styled(FLEX_COLUMN_DIV, { blocked: Boolean })`
    max-height: 100vh;
    height: 100%;
    position: relative;
    padding-left: 28px;
    padding-right: 28px;

    .bottom-border {
        position: relative;
        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: -28px;
            right: -28px;
            height: 1px;
            background-color: ${COLORS['gray-25']};
        }
    }

    @media screen and (max-width: 767px) {
        padding-left: 18px;
        padding-right: 18px;
    }

    ${({ blocked }) =>
        blocked &&
        css`
            filter: url(#blur-filter);
            filter: blur(3.5px);
            pointer-events: none;
        `};
`;

const HeaderArea = styled(FLEX_VERTICAL_CENTER_DIV)`
    height: 70px;
    justify-content: space-between;
    align-items: center;
    position: relative;

    .refresh-icon,
    .close-icon {
        cursor: pointer;
        flex-shrink: 0;
        ${square(24)};
    }

    .close-icon {
        margin-left: 8px;
        ${applyHoverAndPress(COLORS['gray-30'], 'fill')}
    }

    @media screen and (max-width: 767px) {
        padding-top: 16px;
        padding-bottom: 13px;
    }
`;

const HeaderText = styled(GRAY_100_SPAN, { type: String })`
    font-size: 18px;
    font-weight: 500;
    max-width: 450px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    color: ${({ type }) => {
        switch (type) {
            case 'warning':
                return COLORS['red-60'];
            default:
                return COLORS['gray-100'];
        }
    }};
    & .warning-icon {
        fill: ${COLORS['red-60']};
        margin-right: 8px;
    }
    & .close-icon {
        ${applyHoverAndPress(COLORS['gray-30'], 'fill')};
    }
`;

const BottomArea = styled(FLEX_VERTICAL_CENTER_DIV)`
    padding: 16px 16px;
    border-top: 1px solid ${COLORS['gray-25']};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 92px;

    .buttons-area {
        border-top: none;
        width: 100%;
        padding: 0;
    }

    @media screen and (max-width: 767px) {
        height: 136px;
        padding: 20px 18px;
    }
`;

const InfoArea = styled(FLEX_COLUMN_DIV)`
    padding-top: 16px;
    padding-bottom: 16px;
    justify-content: center;
    position: relative;

    @media screen and (max-width: 767px) {
        padding-top: 16px;
        padding-bottom: 16px;
    }
`;

const EditArea = styled(FLEX_COLUMN_DIV, {
    editAreaPadding: String,
    hasXScroll: Boolean,
})`
    flex: 1;
    overflow-y: auto;
    overflow-x: ${({ hasXScroll }) => (hasXScroll ? 'auto' : 'hidden')};
    padding-top: 28px;
    padding-bottom: 28px;
    margin: 0 -28px;
    scrollbar-gutter: stable;

    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${COLORS['gray-25']};
    }

    ${({ editAreaPadding }) =>
        editAreaPadding &&
        css`
            padding: ${editAreaPadding};
        `}

    @media screen and (max-width: 767px) {
        padding-top: 16px;
        padding-bottom: 40px;

        ${({ editAreaPadding }) =>
            editAreaPadding &&
            css`
                padding: ${editAreaPadding};
            `}
    }
`;

const BottomDummy = styled.div`
    height: 92px;
    flex-shrink: 0;

    @media screen and (max-width: 767px) {
        height: 136px;
    }
`;

const LoadingMask = styled.div`
    ${ABSOLUTE_FULL_AREA};
    z-index: 1;
    background-color: rgba(255, 255, 255, 0.5);

    .lds-ring {
        ${ABSOLUTE_BOTH_CENTER};
    }
`;

const LoadingMaskContent = styled(FLEX_COLUMN_DIV)`
    ${ABSOLUTE_BOTH_CENTER};
    align-items: center;
    gap: 16px;
`;

export const LoadingMaskText = styled.span`
    font-size: 14px;
    color: ${COLORS['gray-80']};
    text-align: center;
    margin-top: 8px;
`;
